import AddIcon from '@mui/icons-material/Add';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ListIcon from '@mui/icons-material/List';
import { compact } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { configure, HotKeys } from 'react-hotkeys';
import { useTranslation } from 'react-i18next';
import { createSearchParams } from 'react-router-dom';
import { DeepReadonly } from 'rxdb/dist/types/types';

import { prepareSuggestionEls } from '@work4all/components/lib/components/global-search/prepareSuggestionEls';
import { useEntitySearchData } from '@work4all/components/lib/hooks/useEntitySearchData';
import { useProjectSearchData } from '@work4all/components/lib/hooks/useProjectSearchData';
import { ActionType } from '@work4all/components/lib/input/search-bar-with-fullscreen/types';
import { SearchResultItem } from '@work4all/components/lib/input/search-result-item/SearchResultItem';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { useLocation, useNavigate, useSearchKeyHandlers } from '@work4all/data';
import { useSearchHistory } from '@work4all/data/lib/hooks/use-search-history';
import { useRoutesMetaBag } from '@work4all/data/lib/hooks/useRoutesMetaBag';

import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { Project } from '@work4all/models/lib/Classes/Project.entity';
import { Supplier } from '@work4all/models/lib/Classes/Supplier.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { FileData, FileType } from '@work4all/models/lib/File';

import { sortSearchResults } from '@work4all/utils/lib/search/sortSearchResults';

import { InsertFromClipboard } from '../../../components/entity-picker/InsertFromClipboardModal';
import { MaskModal } from '../../../components/entity-picker/MaskModal';
import { PickFromListModal } from '../../../components/entity-picker/PickFromListModal';
import { usePageTitle } from '../../../hooks/use-page-title';

import { SearchWrapper } from './components/SearchWrapper';
import { useSearchedFiles } from './hooks/useSearchedFiles';
import { NewBusinessPartnerTemplate } from './places-utils';
import { PlaceSuggestions } from './PlacesAutosuggestions';
import { OnElementSelected } from './types';
import { sortFilesData } from './utils/sortFilesData';

interface Props {
  lastSearchItems: DeepReadonly<FileData[]>;
  elementSelected: OnElementSelected;
  defaultActions: ActionType[];
  title: string;
  number: string;
  description: string;
  placeholder: string;
  pathname: string;
  onOpenMask: (template: NewBusinessPartnerTemplate) => void;
}

configure({
  ignoreTags: [],
  ignoreRepeatedEventsWhenKeyHeldDown: false,
  customKeyCodes: {
    13: 'NAVIGATE_TO',
  },
});

const CustomerData: React.FC<Props> = (props) => {
  const {
    isLoading,
    results: { direct: customerList, related: customerContactsList },
    inputValue,
    onChange,
    debouncedValue,
  } = useEntitySearchData<Customer>(Entities.customer);

  const sortedList = sortSearchResults([
    ...customerList,
    ...customerContactsList,
  ]);
  const { setKeyboardLocation, keyMap, keyHandlers } =
    useSearchKeyHandlers(sortedList);

  useEffect(() => {
    document.getElementById(sortedList[0]?.id?.toString())?.scrollIntoView();
    setKeyboardLocation(0);
  }, [inputValue]);

  const list = useMemo(() => {
    const customerCleanedList = compact(customerList);
    const contactCleanedList = compact(customerContactsList);
    return prepareSuggestionEls({
      businessPartnerList: customerCleanedList,
      relatedContactsList: contactCleanedList,
      pathname: props.pathname,
      elementSelected: props.elementSelected,
    });
  }, [
    customerContactsList,
    customerList,
    props.elementSelected,
    props.pathname,
  ]);

  return (
    <HotKeys allowChanges keyMap={keyMap} handlers={keyHandlers}>
      <SearchWrapper
        header={{
          title: props.title,
          description: props.description,
        }}
        actions={props.defaultActions}
        footerActions={props.defaultActions}
        search={{ inputValue, onChange, placeholder: props.placeholder }}
        list={list}
        suggestions={
          <PlaceSuggestions
            entity={Entities.customer}
            disabled={isLoading || list.length > 0}
            query={debouncedValue}
            onSelect={props.onOpenMask}
          />
        }
        history={{
          lastSearchItems: props.lastSearchItems,
          onSelect: (el) => props.elementSelected(el),
        }}
      />
    </HotKeys>
  );
};

const SuppliersData: React.FC<Props> = (props) => {
  const {
    isLoading,
    results: { direct: supplierList, related: supplierContactsList },
    inputValue,
    onChange,
    debouncedValue,
  } = useEntitySearchData<Supplier>(Entities.supplier);

  const sortedList = sortSearchResults([
    ...supplierList,
    ...supplierContactsList,
  ]);
  const { setKeyboardLocation, keyMap, keyHandlers } =
    useSearchKeyHandlers(sortedList);

  useEffect(() => {
    document.getElementById(sortedList[0]?.id?.toString())?.scrollIntoView();
    setKeyboardLocation(0);
  }, [inputValue]);

  const list = useMemo(() => {
    const supplierCleanedList = compact(supplierList);
    const contactCleanedList = compact(supplierContactsList);
    return prepareSuggestionEls({
      businessPartnerList: supplierCleanedList,
      relatedContactsList: contactCleanedList,
      pathname: props.pathname,
      elementSelected: props.elementSelected,
    });
  }, [
    supplierList,
    supplierContactsList,
    props.pathname,
    props.elementSelected,
  ]);

  return (
    <HotKeys allowChanges keyMap={keyMap} handlers={keyHandlers}>
      <SearchWrapper
        header={{
          title: props.title,
          description: props.description,
        }}
        footerActions={props.defaultActions}
        actions={props.defaultActions}
        search={{ inputValue, onChange, placeholder: props.placeholder }}
        list={list}
        suggestions={
          <PlaceSuggestions
            entity={Entities.supplier}
            disabled={isLoading || list.length > 0}
            query={debouncedValue}
            onSelect={props.onOpenMask}
          />
        }
        history={{
          lastSearchItems: props.lastSearchItems,
          onSelect: (el) => props.elementSelected(el),
        }}
      />
    </HotKeys>
  );
};

const ProjectData: React.FC<Props> = (props) => {
  const {
    results: { direct: projectList },
    inputValue,
    onChange,
  } = useProjectSearchData();

  const { setKeyboardLocation, keyMap, keyHandlers } =
    useSearchKeyHandlers(projectList);

  useEffect(() => {
    document.getElementById(projectList[0]?.id?.toString())?.scrollIntoView();
    setKeyboardLocation(0);
  }, [inputValue]);

  const list = useMemo(() => {
    const list = compact(projectList);
    return list.map((item) => {
      return (
        <SearchResultItem
          key={item.id}
          id={item.id?.toString()}
          number={item.number}
          href={`${props.pathname}/${item.id}`}
          title={item.name || ''}
          onClick={() =>
            props.elementSelected({
              id: item.id?.toString() || '',
              number: item.number || '',
              name: item.name || '',
            })
          }
        ></SearchResultItem>
      );
    });
  }, [projectList, props]);

  return (
    <HotKeys allowChanges keyMap={keyMap} handlers={keyHandlers}>
      <SearchWrapper
        header={{
          title: props.title,
          description: props.description,
        }}
        actions={props.defaultActions}
        footerActions={props.defaultActions}
        search={{ inputValue, onChange, placeholder: props.placeholder }}
        list={list}
        history={{
          lastSearchItems: props.lastSearchItems,
          onSelect: (el) => props.elementSelected(el),
        }}
      />
    </HotKeys>
  );
};

export const SearchPage: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { states } = useRoutesMetaBag();
  const currentPath = `/${pathname.split('/')[1]}`;

  // If a user opened a file page before and it hasn't been closed,
  // redirect the user to that page instead of showing the search page.
  useEffect(() => {
    // Remove leading slash from the pathname
    const path = currentPath.slice(1);
    if (states[path]) {
      const params = states[path].params || {};
      const paramsString = Object.keys(params)
        .map((paramName) => {
          return `${paramName}=${params[paramName]}`;
        })
        .join('&');
      navigate(`/${path}/${states[path].id}?${paramsString}`, {
        replace: true,
      });
    }
  }, [currentPath, navigate, states]);

  const [listModalOpen, setListModalOpen] = useState(false);
  const [maskModalOpen, setMaskModalOpen] = useState(false);
  const [defaultValue, setDefaultValue] = useState({});
  const [imprintInputOpen, setImprintInputOpen] = useState(false);

  const pickFromListAction = {
    icon: ListIcon,
    title: '',
    handler: () => {
      sendAmplitudeData(EventType.Search, {
        entryPoint: currentPath.substring(1, currentPath.length),
      });
      setListModalOpen(true);
    },
  };

  const createNewCustomerAction = {
    icon: AddIcon,
    title: 'CUSTOMER.ACTIONS.CREATE_NEW',
    handler: () => {
      setMaskModalOpen(true);
    },
  };

  const createNewSupplierAction = {
    icon: AddIcon,
    title: 'SUPPLIER.ACTIONS.CREATE_NEW',
    handler: () => {
      setMaskModalOpen(true);
    },
  };

  const createNewProjectAction = {
    icon: AddIcon,
    title: 'PROJECT.ACTIONS.CREATE_NEW',
    handler: () => {
      setMaskModalOpen(true);
    },
  };

  const insertCustomerActions = {
    icon: ContentPasteIcon,
    title: '',
    handler: () => {
      setImprintInputOpen(true);
    },
  };
  const insertSupplierActions = {
    icon: ContentPasteIcon,
    title: '',
    handler: () => {
      setImprintInputOpen(true);
    },
  };

  useEffect(() => {
    sendAmplitudeData(EventType.Search, {
      entryPoint: currentPath.substring(1, currentPath.length),
    });
  }, [currentPath]);

  let type: FileType = FileType.PROJECT;
  let entity: Entities = Entities.project;
  let Component = ProjectData;
  switch (currentPath) {
    case '/customers':
      type = FileType.CUSTOMER;
      entity = Entities.customer;
      pickFromListAction.title = 'CUSTOMER.ACTIONS.LIST';
      insertCustomerActions.title = 'CUSTOMER.ACTIONS.INSERT_CUSTOMER';
      Component = CustomerData;
      break;
    case '/suppliers':
      type = FileType.SUPPLIER;
      entity = Entities.supplier;
      pickFromListAction.title = 'SUPPLIER.ACTIONS.LIST';
      insertSupplierActions.title = 'SUPPLIER.ACTIONS.INSERT_SUPPLIER';

      Component = SuppliersData;
      break;
    case '/projects':
      type = FileType.PROJECT;
      entity = Entities.project;
      pickFromListAction.title = 'PROJECT.ACTIONS.LIST';
      Component = ProjectData;
      break;
  }

  usePageTitle(t(`COMMON.${type.toUpperCase()}`, { count: 2 }));

  const { lastSearchItems, saveSearchItem } = useSearchHistory();

  const lastSearchedIds = useMemo(() => {
    return lastSearchItems[type]
      .filter((item) => item?.id)
      .map((item) => item.id.toString());
  }, [lastSearchItems, type]);

  const filesData = useSearchedFiles(
    lastSearchedIds,
    type,
    entity !== Entities.project ? { website: null } : {}
  );

  const sortedLastSearchedData = useMemo(() => {
    return sortFilesData(filesData.resData.data, lastSearchedIds);
  }, [filesData, lastSearchedIds]);

  const compProps: Props = {
    lastSearchItems: sortedLastSearchedData,
    pathname: `${currentPath}`,
    defaultActions:
      currentPath === '/customers'
        ? [createNewCustomerAction, insertCustomerActions, pickFromListAction]
        : currentPath === '/suppliers'
        ? [createNewSupplierAction, insertSupplierActions, pickFromListAction]
        : [createNewProjectAction, pickFromListAction],
    number: '0',
    elementSelected: (el) => {
      saveSearchItem(entity, el);
    },
    title: t(`${type.toUpperCase()}.PAGE_TITLE`),
    description: t(`${type.toUpperCase()}.PAGE_DESCRIPTION`),
    placeholder: t(`${type.toUpperCase()}.SEARCH_PLACEHOLDER`),
    onOpenMask(template) {
      setDefaultValue(template);
      setMaskModalOpen(true);
    },
  };

  return (
    <>
      <PickFromListModal<Customer | Supplier | Project>
        open={listModalOpen}
        entity={entity}
        multiple
        onClose={() => {
          setListModalOpen(false);
        }}
        onConfirm={(selected) => {
          saveSearchItem(entity, {
            id: selected[0].id.toString(),
            number: selected[0].number.toString(),
            website: (selected[0] as Customer | Supplier).website,
            name: selected[0].name,
          });

          setListModalOpen(false);

          const ids = selected.map((entity) => String(entity.id));
          navigate({
            pathname: ids[0],
            search: ids.length > 1 ? `${createSearchParams({ ids })}` : null,
          });
        }}
      />
      <MaskModal
        entity={entity}
        params={{ presetFields: JSON.stringify(defaultValue) }}
        open={maskModalOpen}
        onClose={() => {
          setMaskModalOpen(false);
        }}
        onAfterSave={(result: Customer | Supplier | Project) => {
          setMaskModalOpen(false);

          // TODO This will redirect to the new entity page before the mask
          // close animation is finished.
          navigate(`${result.id}`);
        }}
      />

      {imprintInputOpen && (
        <InsertFromClipboard
          entity={entity}
          onSuccess={(value) => {
            setDefaultValue(value);
            sendAmplitudeData(EventType.AddFileFromImprint, {
              entryPoint: currentPath.substring(1, currentPath.length),
            });
            setMaskModalOpen(true);
            setImprintInputOpen(false);
          }}
          onClose={() => setImprintInputOpen(false)}
        />
      )}
      <Component {...compProps} />
    </>
  );
};
